/**
 * Project feature access engine.
 *
 * Single source of truth for which billingTiers unlock each project feature, and whether a tier
 * is enabled for an org. Pure + dependency-light so it is shared by the API and the client.
 *
 * Per-org overrides live on the `subscriptions` row (`featureAccess`, `enabledBillingTiers`) and are
 * SPARSE: only deviations from these code defaults are stored. Anything unset falls through here, so
 * adding a feature is one line below — no DB backfill.
 */

/** Mirrors the db `BillingTier` pgEnum. Kept local to avoid a utils->db->utils dependency cycle. */
export type FeatureBillingTier = 'draft' | 'proposal' | 'project' | 'projectPlus';

export enum ProjectFeature {
  Downloads = 'downloads',
  Potholing = 'potholing',
  Digitization = 'digitization',
  CivilSearch = 'civilSearch',
  Intelligence = 'intelligence',
  LandRights = 'landRights',
  Zoning = 'zoning',
  AntennaMap = 'antennaMap',
  EnvironmentalStudies = 'environmentalStudies',
  Geotechnical = 'geotechnical',
  Environmental = 'environmental',
  FieldNotes = 'fieldNotes',
  Utilities = 'utilities',
  UtilitiesReports = 'utilitiesReports',
  UtilitiesNotifications = 'utilitiesNotifications',
  ProposalPacket = 'proposalPacket',
  Billing = 'billing',
  Summary = 'summary',
}

/**
 * THE default availability per feature. Adding a feature = add a value to ProjectFeature and an
 * entry here. The `Record<ProjectFeature, ...>` type makes a missing entry a compile error.
 */
export const FEATURE_DEFAULTS: Record<ProjectFeature, FeatureBillingTier[]> = {
  [ProjectFeature.Downloads]: ['project', 'projectPlus'],
  [ProjectFeature.Potholing]: ['project', 'projectPlus'],
  [ProjectFeature.Digitization]: ['projectPlus'],
  [ProjectFeature.CivilSearch]: ['project', 'projectPlus'],
  [ProjectFeature.Intelligence]: ['project', 'projectPlus'],
  [ProjectFeature.LandRights]: ['proposal', 'project', 'projectPlus'],
  [ProjectFeature.Zoning]: ['project', 'projectPlus'],
  // Off for all tiers by default (opt-in per org via an override).
  [ProjectFeature.AntennaMap]: [],
  [ProjectFeature.EnvironmentalStudies]: [],
  [ProjectFeature.Geotechnical]: ['project', 'projectPlus'],
  [ProjectFeature.Environmental]: ['project', 'projectPlus'],
  [ProjectFeature.FieldNotes]: ['proposal', 'project', 'projectPlus'],
  [ProjectFeature.Utilities]: ['proposal', 'project', 'projectPlus'],
  [ProjectFeature.UtilitiesReports]: ['proposal', 'project', 'projectPlus'],
  [ProjectFeature.UtilitiesNotifications]: ['project', 'projectPlus'],
  [ProjectFeature.ProposalPacket]: ['proposal', 'project', 'projectPlus'],
  [ProjectFeature.Billing]: ['proposal', 'project', 'projectPlus'],
  [ProjectFeature.Summary]: ['draft', 'proposal'],
};

export const DEFAULT_TIER_ENABLED: Record<FeatureBillingTier, boolean> = {
  draft: true,
  proposal: true,
  project: true,
  projectPlus: true,
};

/**
 * Features whose tier availability is permanently HARDCODED to FEATURE_DEFAULTS and can never be
 * configured per-org. They are excluded from the admin matrix, never persisted, and any stored
 * override is ignored by the resolver.
 */
export const NON_CONFIGURABLE_FEATURES: ReadonlySet<ProjectFeature> = new Set([
  ProjectFeature.CivilSearch,
  ProjectFeature.Digitization,
]);

export function isFeatureConfigurable(feature: ProjectFeature): boolean {
  return !NON_CONFIGURABLE_FEATURES.has(feature);
}

/**
 * Tiers that can never be disabled per-org — they are always enabled. `draft` is the baseline entry
 * point every org needs, so its toggle is hidden in the admin matrix, any stored override is ignored
 * by the resolver, and a disable attempt is stripped before it is persisted.
 */
export const NON_DISABLEABLE_TIERS: ReadonlySet<FeatureBillingTier> = new Set(['draft']);

export function isTierDisableable(tier: FeatureBillingTier): boolean {
  return !NON_DISABLEABLE_TIERS.has(tier);
}

/**
 * The subset of a subscription this engine reads. Both columns are optional/sparse.
 *
 * Values are typed as plain `string[]` / `Record<string, ...>` because the data originates from a
 * loosely-typed JSONB column (`Partial<Record<string, BillingTier[]>>` on the db side, to avoid a
 * utils->db->utils cycle). The resolver only checks membership, so raw strings are safe.
 */
export interface SubscriptionFeatureConfig {
  featureAccess?: Partial<Record<string, string[]>> | null;
  enabledBillingTiers?: Partial<Record<string, boolean>> | null;
}

export function isBillingTierEnabled(
  tier: FeatureBillingTier,
  subscription?: SubscriptionFeatureConfig | null,
): boolean {
  // Non-disableable tiers (e.g. draft) ignore any stored override and always use the code default.
  const stored = isTierDisableable(tier) ? subscription?.enabledBillingTiers?.[tier] : undefined;
  return stored ?? DEFAULT_TIER_ENABLED[tier];
}

export function isFeatureEnabled(
  feature: ProjectFeature,
  tier: FeatureBillingTier,
  subscription?: SubscriptionFeatureConfig | null,
): boolean {
  // Non-configurable features ignore any stored override and always use the code default.
  const override = isFeatureConfigurable(feature) ? subscription?.featureAccess?.[feature] : undefined;
  const tiers = override ?? FEATURE_DEFAULTS[feature];
  // NOTE: intentionally NOT gated by isBillingTierEnabled. `enabledBillingTiers` controls whether a
  // tier can be SELECTED (new project / upgrade — enforced in the tier-selector UIs), not whether an
  // existing project already on that tier keeps working. Gating here would brick every tab of a
  // project whose tier the org later disabled (infinite redirect loop). Feature availability is
  // purely the per-tier matrix.
  return tiers.includes(tier);
}

/** Resolved view for the admin UI: each feature's effective tiers + whether it is an override. */
export function resolveFeatureMatrix(
  subscription?: SubscriptionFeatureConfig | null,
): Array<{ feature: ProjectFeature; tiers: FeatureBillingTier[]; isOverride: boolean }> {
  return Object.values(ProjectFeature).map((feature) => {
    // Override values originate from a loosely-typed JSONB column but are always valid tier strings.
    // Non-configurable features ignore any stored override.
    const override = isFeatureConfigurable(feature)
      ? (subscription?.featureAccess?.[feature] as FeatureBillingTier[] | undefined)
      : undefined;
    return {
      feature,
      tiers: override ?? FEATURE_DEFAULTS[feature],
      isOverride: override !== undefined,
    };
  });
}

export const ALL_BILLING_TIERS: readonly FeatureBillingTier[] = ['draft', 'proposal', 'project', 'projectPlus'];

type ParseResult<T> = { ok: true; value: T | undefined } | { ok: false; error: string };

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Validates an untrusted `featureAccess` payload (admin API input) against the ProjectFeature and
 * tier allowlists before it reaches the jsonb column. A malformed stored value would crash
 * `isFeatureEnabled` (`.includes` on a non-array) at request time across the whole app, so writes
 * must go through here. Absent input resolves to `undefined` (keep the stored value); non-configurable
 * features are stripped rather than persisted; tier arrays are deduped.
 */
export function parseFeatureAccess(input: unknown): ParseResult<Partial<Record<string, FeatureBillingTier[]>>> {
  if (input === undefined || input === null) return { ok: true, value: undefined };
  if (!isPlainObject(input)) return { ok: false, error: 'featureAccess must be an object' };
  const validFeatures = new Set<string>(Object.values(ProjectFeature));
  const value: Partial<Record<string, FeatureBillingTier[]>> = {};
  for (const [feature, tiers] of Object.entries(input)) {
    if (!validFeatures.has(feature)) return { ok: false, error: `Unknown feature "${feature}"` };
    if (!isFeatureConfigurable(feature as ProjectFeature)) continue;
    if (!Array.isArray(tiers) || tiers.some((tier) => !ALL_BILLING_TIERS.includes(tier as FeatureBillingTier))) {
      return { ok: false, error: `featureAccess.${feature} must be an array of billing tiers` };
    }
    value[feature] = [...new Set(tiers as FeatureBillingTier[])];
  }
  return { ok: true, value };
}

/** Same contract as parseFeatureAccess, for the `enabledBillingTiers` payload. */
export function parseEnabledBillingTiers(input: unknown): ParseResult<Partial<Record<FeatureBillingTier, boolean>>> {
  if (input === undefined || input === null) return { ok: true, value: undefined };
  if (!isPlainObject(input)) return { ok: false, error: 'enabledBillingTiers must be an object' };
  const value: Partial<Record<FeatureBillingTier, boolean>> = {};
  for (const [tier, enabled] of Object.entries(input)) {
    if (!ALL_BILLING_TIERS.includes(tier as FeatureBillingTier)) {
      return { ok: false, error: `Unknown billing tier "${tier}"` };
    }
    // Non-disableable tiers (e.g. draft) are always enabled; drop any attempt to persist a value.
    if (!isTierDisableable(tier as FeatureBillingTier)) continue;
    if (typeof enabled !== 'boolean') {
      return { ok: false, error: `enabledBillingTiers.${tier} must be a boolean` };
    }
    value[tier as FeatureBillingTier] = enabled;
  }
  return { ok: true, value };
}
